(() => {
  const STORAGE_KEY = 'koyo-frequent-list-v1';
  const validId = (id) => /^\d{1,16}$/.test(String(id)) && Number.isSafeInteger(Number(id)) && Number(id) > 0;
  const validQuantity = (quantity) => Number.isInteger(quantity) && quantity > 0 && quantity <= 9999;
  const validHandle = (handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle);
  const plainItem = (item) => !item.selling_plan_allocation && !item.selling_plan &&
    !item.parent_relationship && !item.item_components?.length &&
    (!item.properties || Object.keys(item.properties).length === 0);

  function validateList(value) {
    if (!value || value.version !== 1 || !Array.isArray(value.items) || !value.items.length || value.items.length > 50) {
      throw new Error('invalid');
    }
    const ids = new Set();
    const items = value.items.map((item) => {
      if (!item || !validId(item.id) || !validQuantity(item.quantity) || !validHandle(item.handle) ||
          typeof item.title !== 'string' || item.title.length > 500 || !Array.isArray(item.options) ||
          item.options.length > 3 || item.options.some((option) => typeof option !== 'string' || option.length > 250) || ids.has(String(item.id))) {
        throw new Error('invalid');
      }
      ids.add(String(item.id));
      return { id: String(item.id), quantity: item.quantity, handle: item.handle, title: item.title, options: item.options };
    });
    return { version: 1, items };
  }

  function fromCart(cart) {
    if (!Array.isArray(cart.items) || !cart.items.length) throw new Error('empty');
    if (cart.items.some((item) => !plainItem(item))) throw new Error('unsupported');
    const items = new Map();
    for (const item of cart.items) {
      const choice = validateList({ version: 1, items: [{
        id: String(item.variant_id), quantity: item.quantity, handle: item.handle,
        title: item.product_title, options: (item.options_with_values || []).map((option) => option.value),
      }] }).items[0];
      const previous = items.get(choice.id);
      if (previous) {
        if (previous.handle !== choice.handle || previous.title !== choice.title ||
            JSON.stringify(previous.options) !== JSON.stringify(choice.options)) throw new Error('invalid');
        previous.quantity += choice.quantity;
      } else items.set(choice.id, choice);
    }
    return validateList({ version: 1, items: [...items.values()] });
  }

  function missingItems(saved, cart) {
    if (!Array.isArray(cart.items)) throw new Error('read_error');
    return saved.items.map((item) => {
      const matches = cart.items.filter((line) => String(line.variant_id) === item.id);
      if (matches.some((line) => !plainItem(line))) throw new Error('unsupported');
      const current = matches.reduce((sum, line) => {
        if (!validQuantity(line.quantity)) throw new Error('read_error');
        return sum + line.quantity;
      }, 0);
      return { ...item, current, missing: Math.max(0, item.quantity - current) };
    });
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { validateList, fromCart, missingItems, plainItem };
  if (typeof window === 'undefined' || customElements.get('koyo-reorder-list')) return;

  customElements.define('koyo-reorder-list', class extends HTMLElement {
    connectedCallback() {
      this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
      this.buttons = [...this.querySelectorAll('button[data-action]')];
      this.reviewPanel = this.querySelector('[data-review]');
      this.status = this.querySelector('[data-status]');
      this.root = window.Shopify?.routes?.root || '/';
      if (!this.root.startsWith('/') || this.root.startsWith('//')) this.root = '/';
      this.addEventListener('click', this.onClick = (event) => {
        const button = event.target.closest('button[data-action]');
        if (button && this.contains(button) && !button.disabled) this.run(button.dataset.action);
      });
      this.onStorage = (event) => {
        if (event.key === STORAGE_KEY) {
          this.reviewed = null;
          this.reviewPanel.hidden = true;
          this.refreshSaved();
        }
      };
      window.addEventListener('storage', this.onStorage);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.reviewed = null;
          this.reviewPanel.hidden = true;
          this.updateButtons();
          const previous = this.querySelector('.koyo-reorder-list__export');
          const html = event.cartData?.sections?.[this.dataset.sectionId];
          const next = html && new DOMParser().parseFromString(html, 'text/html').querySelector('.koyo-reorder-list__export');
          previous?.remove();
          if (next) this.append(next);
          this.updateDownload();
        });
      }
      this.querySelector('[data-controls]').hidden = false;
      this.refreshSaved();
      this.updateDownload();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('storage', this.onStorage);
      this.unsubscribeCart?.();
      this.releaseDownload();
    }

    releaseDownload() {
      if (this.downloadAnchor && this.downloadFallback) this.downloadAnchor.setAttribute('href', this.downloadFallback);
      if (this.downloadUrl) URL.revokeObjectURL(this.downloadUrl);
      this.downloadUrl = null;
      this.downloadAnchor = null;
      this.downloadFallback = null;
    }

    updateDownload() {
      this.releaseDownload();
      const anchor = this.querySelector('.koyo-reorder-list__export a[download]');
      const textarea = this.querySelector('.koyo-reorder-list__export textarea');
      if (!anchor || !textarea || typeof Blob !== 'function' || typeof URL === 'undefined' ||
          typeof URL.createObjectURL !== 'function' || typeof URL.revokeObjectURL !== 'function') return;
      try {
        this.downloadUrl = URL.createObjectURL(new Blob([textarea.value], { type: 'text/plain;charset=utf-8' }));
        this.downloadAnchor = anchor;
        this.downloadFallback = anchor.getAttribute('href');
        anchor.setAttribute('href', this.downloadUrl);
      } catch {
        this.releaseDownload();
      }
    }

    text(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, String(value)), this.messages[key] || key);
    }

    tell(key, values) { this.status.textContent = this.text(key, values); }

    readSaved() {
      let raw;
      try { raw = localStorage.getItem(STORAGE_KEY); } catch { throw new Error('storage_error'); }
      if (!raw) return null;
      if (raw.length > 100000) throw new Error('invalid');
      try { return validateList(JSON.parse(raw)); } catch { throw new Error('invalid'); }
    }

    refreshSaved() {
      try {
        this.saved = this.readSaved();
        this.tell(this.saved ? 'saved' : 'no_saved', { count: this.saved?.items.length });
      } catch (error) {
        this.saved = null;
        this.tell(error.message);
      }
      this.updateButtons();
    }

    updateButtons() {
      this.buttons.forEach((button) => {
        const action = button.dataset.action;
        button.disabled = this.busy || (action === 'review' && !this.saved) ||
          (action === 'add' && (!this.reviewed || !this.reviewed.ready));
        if (action === 'save') button.textContent = this.text(this.saved ? 'replace' : 'save');
      });
      this.setAttribute('aria-busy', this.busy ? 'true' : 'false');
    }

    async request(path, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(this.root + path, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        if (!response.ok) throw new Error('read_error');
        return await response.json();
      } finally { clearTimeout(timer); }
    }

    async resolve() {
      const saved = this.readSaved();
      if (!saved) throw new Error('no_saved');
      const products = new Map();
      await Promise.all([...new Set(saved.items.map((item) => item.handle))].map(async (handle) => {
        const product = await this.request(`products/${encodeURIComponent(handle)}.js`).catch(() => null);
        products.set(handle, product);
      }));
      const cart = await this.request('cart.js');
      if (!/^[A-Z]{3}$/.test(cart.currency)) throw new Error('read_error');
      const rows = missingItems(saved, cart).map((item) => {
        const product = products.get(item.handle);
        const variant = product?.variants?.find((variant) => String(variant.id) === item.id);
        const price = variant?.price;
        const rule = variant?.quantity_rule;
        const validRule = !rule || (item.quantity >= (rule.min || 1) &&
          (rule.max == null || item.quantity <= rule.max) && item.quantity % (rule.increment || 1) === 0);
        return { ...item, title: product?.title || item.title, options: variant?.options || [],
          price, available: !!variant?.available && Number.isSafeInteger(price) && price >= 0 &&
            !product?.requires_selling_plan && !variant?.requires_selling_plan && validRule };
      });
      const ready = rows.every((row) => row.available) && rows.some((row) => row.missing > 0);
      return { rows, currency: cart.currency, ready };
    }

    renderReview(review) {
      this.reviewed = review;
      const list = this.querySelector('[data-items]');
      list.replaceChildren();
      const money = (price) => new Intl.NumberFormat(document.documentElement.lang || undefined,
        { style: 'currency', currency: review.currency }).format(price / 100);
      review.rows.forEach((row) => {
        const line = document.createElement('li');
        const link = document.createElement('a');
        link.href = `${this.root}products/${encodeURIComponent(row.handle)}?variant=${row.id}`;
        link.textContent = row.title;
        line.append(link);
        const details = document.createElement('p');
        details.textContent = row.options.filter((option) => option !== 'Default Title').join(' · ');
        if (details.textContent) line.append(details);
        const packs = row.options.filter((option) => /^Pack of (5|10|100)$/.test(option));
        const quantity = document.createElement('p');
        quantity.textContent = this.text(packs.length === 1 ? 'packs' : 'quantity', { count: row.quantity });
        if (packs.length === 1) quantity.textContent += ` · ${this.text('burs', { count: row.quantity * Number(packs[0].slice(8)) })}`;
        quantity.textContent += ` · ${this.text('in_cart', { count: row.current })} · ${this.text('to_add', { count: row.missing })}`;
        line.append(quantity);
        const price = document.createElement('p');
        price.textContent = row.available ? `${this.text('unit_price', { price: money(row.price) })} · ${money(row.price * row.quantity)}` : this.text('unavailable');
        line.append(price);
        list.append(line);
      });
      const available = review.rows.every((row) => row.available);
      this.querySelector('[data-total]').textContent = available ?
        `${this.text('total', { price: money(review.rows.reduce((sum, row) => sum + row.price * row.quantity, 0)) })} · ${this.text('missing_total', { price: money(review.rows.reduce((sum, row) => sum + row.price * row.missing, 0)) })}` : this.text('missing_product');
      this.reviewPanel.hidden = false;
      this.tell(!available ? 'missing_product' : review.ready ? 'review_ready' : 'already_added');
    }

    async run(action) {
      if (this.busy) return;
      this.busy = true;
      this.updateButtons();
      this.tell(action === 'add' ? 'adding' : 'loading');
      try {
        if (action === 'save') {
          const saved = fromCart(await this.request('cart.js'));
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(saved)); } catch { throw new Error('storage_error'); }
          this.saved = saved;
          this.reviewed = null;
          this.reviewPanel.hidden = true;
          this.tell('saved', { count: saved.items.length });
        } else if (action === 'remove') {
          try { localStorage.removeItem(STORAGE_KEY); } catch { throw new Error('storage_error'); }
          this.saved = null;
          this.reviewed = null;
          this.reviewPanel.hidden = true;
          this.tell('removed');
        } else if (action === 'review') {
          this.reviewed = null;
          this.renderReview(await this.resolve());
        } else if (action === 'add') {
          const before = JSON.stringify(this.reviewed);
          this.reviewed = null;
          const fresh = await this.resolve();
          this.renderReview(fresh);
          if (JSON.stringify(fresh) !== before) { this.tell('changed'); return; }
          if (!fresh.ready) return;
          this.reviewed = null;
          await this.request('cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: fresh.rows.filter((row) => row.missing > 0).map((row) => ({ id: Number(row.id), quantity: row.missing })) }),
          });
          window.location.assign(this.dataset.cartUrl);
          return;
        }
      } catch (error) {
        this.reviewed = null;
        this.reviewPanel.hidden = true;
        if (action === 'add') {
          await this.request('cart.js').catch(() => null);
          this.tell('add_error');
        } else this.tell(this.messages[error.message] ? error.message : 'read_error');
      } finally {
        this.busy = false;
        this.updateButtons();
      }
    }
  });
})();
